/**
 * GuardSummaryModal
 *
 * Modal dialog showing a guard's current shift overview with patrol list.
 * Opens when clicking "View Details" on map markers or "Details" on guard cards.
 *
 * Visual Design:
 * ┌─────────────────────────────────────────┐
 * │ [X]  Guard Summary                      │
 * ├─────────────────────────────────────────┤
 * │ ┌─────────────────────────────────────┐ │
 * │ │ 👤 John Smith                       │ │
 * │ │ Status: Active ●                    │ │
 * │ │ Battery: 85%                        │ │
 * │ │ Last seen: 2 min ago                │ │
 * │ └─────────────────────────────────────┘ │
 * │                                         │
 * │ Current Shift (3 patrols)               │
 * │ 8:00 AM - ongoing                       │
 * │                                         │
 * │ ┌─────────────────────────────────────┐ │
 * │ │ ✓ Downtown Office    8:00-9:30 AM  │→│
 * │ │   Completed • 12/12 checkpoints     │ │
 * │ ├─────────────────────────────────────┤ │
 * │ │ ▶ Riverside Plaza    10:00 AM -    │→│
 * │ │   In Progress • 5/8 checkpoints     │ │
 * │ ├─────────────────────────────────────┤ │
 * │ │ ○ Harbor View        12:00 PM      │→│
 * │ │   Scheduled                         │ │
 * │ └─────────────────────────────────────┘ │
 * └─────────────────────────────────────────┘
 */

import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import Battery60Icon from '@mui/icons-material/Battery60';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import classNames from 'classnames';
import styles from './GuardSummaryModal.module.css';
import { useGuardShift } from '../../hooks/dispatch/useGuardShift';
import { useDispatchAlerts } from '../../hooks/dispatch/useDispatchAlerts';
import { ShiftPatrol, PatrolStatus } from '../../../../../api/dispatch/types';
import { GuardLocation, GuardStatus, STATUS_LABELS } from './types';

// ============================================
// Types
// ============================================

export interface GuardSummaryModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** The guard to display (from useGuardLocations) */
  guard: GuardLocation | null;
  /** Callback when user clicks a patrol row to view details */
  onViewPatrolDetails: (scheduleAssignmentId: number, patrol: ShiftPatrol) => void;
  /** Callback when user clicks "Schedule Patrol" to navigate to the scheduler */
  onSchedulePatrol?: (guard: GuardLocation) => void;
}

// ============================================
// Helper Functions
// ============================================

/** Format time for display (e.g., "8:00 AM") */
function formatTime(isoString: string | null): string {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** Format time range for display (e.g., "8:00 - 9:30 AM" or "10:00 AM -") */
function formatTimeRange(
  start: string | null,
  end: string | null,
  status: PatrolStatus
): string {
  const startTime = formatTime(start);
  const endTime = formatTime(end);

  if (status === 'pending') {
    return startTime;
  }

  if (status === 'in_progress') {
    return `${startTime} -`;
  }

  return `${startTime} - ${endTime}`;
}

/** Get patrol status label */
function getStatusLabel(status: PatrolStatus): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'in_progress':
      return 'In Progress';
    case 'pending':
      return 'Scheduled';
    default:
      return '';
  }
}

/** Get battery icon based on percentage */
function getBatteryIcon(percent: number | null): React.ElementType {
  if (percent === null) return BatteryFullIcon;
  if (percent <= 10) return BatteryAlertIcon;
  if (percent <= 30) return Battery20Icon;
  if (percent <= 70) return Battery60Icon;
  return BatteryFullIcon;
}

// ============================================
// Sub-components
// ============================================

interface PatrolRowProps {
  patrol: ShiftPatrol;
  onClick: () => void;
  onStartPatrol?: (patrolId: number) => void;
  isStarting?: boolean;
}

const PatrolRow: React.FC<PatrolRowProps> = ({
  patrol,
  onClick,
  onStartPatrol,
  isStarting = false,
}) => {
  // Choose icon based on status
  const StatusIcon =
    patrol.status === 'completed'
      ? CheckCircleIcon
      : patrol.status === 'in_progress'
      ? PlayArrowIcon
      : RadioButtonUncheckedIcon;

  // Format time display
  const timeDisplay = formatTimeRange(
    patrol.actualStart || patrol.scheduledStart,
    patrol.actualEnd || patrol.scheduledEnd,
    patrol.status
  );

  // Format checkpoints display
  const checkpointsDisplay =
    patrol.checkpointsTotal > 0
      ? `${patrol.checkpointsCompleted}/${patrol.checkpointsTotal} checkpoints`
      : null;

  // Handle start patrol button click
  const handleStartClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the row click
    if (onStartPatrol) {
      onStartPatrol(patrol.scheduleAssignmentId);
    }
  };

  return (
    <Box className={styles.patrolRow} onClick={onClick} role="button" tabIndex={0}>
      <Box
        className={classNames(styles.patrolStatusIcon, {
          [styles.completed]: patrol.status === 'completed',
          [styles.inProgress]: patrol.status === 'in_progress',
          [styles.pending]: patrol.status === 'pending',
        })}
      >
        <StatusIcon />
      </Box>

      <Box className={styles.patrolContent}>
        <Typography className={styles.patrolSite}>{patrol.siteName}</Typography>
        <Box className={styles.patrolMeta}>
          <span className={styles.patrolTime}>{timeDisplay}</span>
          {checkpointsDisplay && (
            <>
              <span className={styles.checkpointDot} />
              <span className={styles.patrolCheckpoints}>{checkpointsDisplay}</span>
            </>
          )}
        </Box>
      </Box>

      {/* Start Patrol button for pending patrols */}
      {patrol.status === 'pending' && onStartPatrol && (
        <Tooltip title="Start patrol on behalf of guard">
          <IconButton
            className={styles.startPatrolButton}
            onClick={handleStartClick}
            disabled={isStarting}
            size="small"
            color="primary"
            aria-label="Start patrol"
          >
            {isStarting ? (
              <CircularProgress size={20} />
            ) : (
              <PlayCircleOutlineIcon />
            )}
          </IconButton>
        </Tooltip>
      )}

      <ChevronRightIcon className={styles.patrolChevron} />
    </Box>
  );
};

// ============================================
// Main Component
// ============================================

const GuardSummaryModal: React.FC<GuardSummaryModalProps> = ({
  open,
  onClose,
  guard,
  onViewPatrolDetails,
  onSchedulePatrol,
}) => {
  // Track which patrol is being started (for loading state)
  const [startingPatrolId, setStartingPatrolId] = useState<number | null>(null);

  // Fetch shift data when modal is open and guard is provided
  const {
    guardName,
    shiftStart,
    shiftEnd,
    patrols,
    totalPatrols,
    completedPatrols,
    isLoading,
    isError,
    error,
    refetch,
  } = useGuardShift({
    guardId: guard?.guardId ?? null,
    enabled: open && guard !== null,
  });

  // Get startPatrol mutation from dispatch alerts hook
  const { startPatrol, isStarting } = useDispatchAlerts({
    disablePolling: true, // Don't poll, just use the mutation
  });

  // Handle patrol row click
  const handlePatrolClick = useCallback(
    (patrol: ShiftPatrol) => {
      onViewPatrolDetails(patrol.scheduleAssignmentId, patrol);
    },
    [onViewPatrolDetails]
  );

  // Handle start patrol
  const handleStartPatrol = useCallback(
    async (patrolId: number) => {
      setStartingPatrolId(patrolId);
      try {
        await startPatrol(patrolId);
        // Refetch shift data to show updated status
        refetch();
      } catch (err) {
        console.error('[GuardSummaryModal] Failed to start patrol:', err);
      } finally {
        setStartingPatrolId(null);
      }
    },
    [startPatrol, refetch]
  );

  // Get battery icon
  const BatteryIcon = getBatteryIcon(guard?.batteryPercent ?? null);

  // Format shift time display
  const shiftTimeDisplay = shiftStart
    ? shiftEnd
      ? `${formatTime(shiftStart)} - ${formatTime(shiftEnd)}`
      : `${formatTime(shiftStart)} - ongoing`
    : '';

  // Map guard status to CSS class
  const getStatusDotClass = (status: GuardStatus): string => {
    switch (status) {
      case 'active':
        return styles.active;
      case 'stale':
        return styles.stale;
      case 'offline':
        return styles.offline;
      case 'not_on_patrol':
        return styles.notOnPatrol;
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '85vh' },
      }}
    >
      {/* Header bar */}
      <Box className={styles.headerBar} />

      {/* Dialog title */}
      <DialogTitle className={styles.dialogTitle}>
        <Typography className={styles.titleText}>Guard Summary</Typography>
        <IconButton
          className={styles.closeButton}
          onClick={onClose}
          size="small"
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        {/* Guard Header */}
        {guard && (
          <Box className={styles.guardHeader}>
            <Typography className={styles.guardName}>
              <PersonIcon />
              {guard.name}
            </Typography>

            <Box className={styles.guardInfo}>
              {/* Status */}
              <Box className={styles.infoItem}>
                <span
                  className={classNames(
                    styles.statusDot,
                    getStatusDotClass(guard.status)
                  )}
                />
                <span>{STATUS_LABELS[guard.status]}</span>
              </Box>

              {/* Battery */}
              {guard.batteryPercent !== null && (
                <Box className={styles.infoItem}>
                  <BatteryIcon
                    sx={{
                      color:
                        guard.batteryPercent <= 20
                          ? 'error.main'
                          : guard.batteryPercent <= 50
                          ? 'warning.main'
                          : 'success.main',
                    }}
                  />
                  <span>{guard.batteryPercent}%</span>
                </Box>
              )}

              {/* Last seen */}
              <Box className={styles.infoItem}>
                <AccessTimeIcon />
                <span>{guard.lastSeenFormatted}</span>
              </Box>
            </Box>
          </Box>
        )}

        {/* Loading state */}
        {isLoading && (
          <Box className={styles.loadingContainer}>
            <CircularProgress size={32} />
            <Typography color="textSecondary">Loading shift data...</Typography>
          </Box>
        )}

        {/* Error state */}
        {isError && !isLoading && (
          <Box className={styles.errorContainer}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            >
              {error?.message || 'Failed to load shift data'}
            </Alert>
          </Box>
        )}

        {/* Shift content */}
        {!isLoading && !isError && (
          <>
            {/* Shift Summary */}
            <Box className={styles.shiftSummary}>
              <Typography className={styles.shiftTitle}>
                Current Shift ({totalPatrols} patrol{totalPatrols !== 1 ? 's' : ''})
              </Typography>
              {shiftTimeDisplay && (
                <Typography className={styles.shiftTime}>{shiftTimeDisplay}</Typography>
              )}
            </Box>

            {/* Patrol List */}
            {patrols.length > 0 ? (
              <Box className={styles.patrolList}>
                {patrols.map((patrol) => (
                  <PatrolRow
                    key={patrol.scheduleAssignmentId}
                    patrol={patrol}
                    onClick={() => handlePatrolClick(patrol)}
                    onStartPatrol={handleStartPatrol}
                    isStarting={startingPatrolId === patrol.scheduleAssignmentId}
                  />
                ))}
              </Box>
            ) : (
              <Box className={styles.emptyState}>
                <EventBusyIcon />
                <Typography>No patrols scheduled for today</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {onSchedulePatrol && (
          <Tooltip title={!guard ? 'No guard selected' : ''}>
            <span>
              <Button
                onClick={() => guard && onSchedulePatrol(guard)}
                variant="contained"
                color="primary"
                startIcon={<CalendarTodayIcon />}
                disabled={!guard}
              >
                Schedule Patrol
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GuardSummaryModal;
